import {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {createSelector} from 'reselect';
import {makeWorkspaceQueryKey} from 'src/modules/profile/user/tabs/content/workspace/util';
import {getUserWorkspaces} from 'src/openapi/admin/apis/UserWorkspacesApi';
import {WorkspaceGeneralInfo} from 'src/openapi/admin/models/WorkspaceGeneralInfo';
import {useAppDispatch} from 'src/store/hooks';

import {
    makeSelectWorkspacesByKey,
    selectReceivedAt,
} from '../slices/workspacesSelectors';
import {UserPageState, defaultWorkspaceCriteria, invalidateQuery} from '../slices/workspacesSlice';


import {useWorkspaceCriteria} from './useWorkspaceCriteria';

type Props = {
    scopeKey: string,
    userId: number,
    autoload?: boolean,
    ttlMs?: number,
};

export function useUserWorkspaces({scopeKey, userId, autoload = true, ttlMs = 120_000}: Props) {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const {criteria, setCriteria, setPage, setSort, reset, setPageSize} = useWorkspaceCriteria(scopeKey);

    useEffect(() => {
        if (!criteria) {
            setCriteria(defaultWorkspaceCriteria);
        }
    }, [criteria, setCriteria]);

    const queryKey = useMemo(
        () => (criteria ? makeWorkspaceQueryKey(userId, criteria) : ''),
        [userId, criteria],
    );

    const selectWorkspaces = useMemo(
        () =>
            queryKey
                ? makeSelectWorkspacesByKey(queryKey)
                : createSelector([], () => ({
                        items: [] as WorkspaceGeneralInfo[],
                        total: 0,
                        status: 'idle' as const,
                        error: undefined,
                    })),
        [queryKey],
    );

    const {items, total, status, error} = useSelector(selectWorkspaces);

    const refetch = useCallback(() => {
        if (!criteria) {
            return;
        }
        apiDispatch(getUserWorkspaces({userId, workspaceRequest: criteria}));
    }, [apiDispatch, userId, criteria]);

    const invalidate = useCallback(() => {
        if (queryKey) {
            dispatch(invalidateQuery(queryKey));
        }
    }, [dispatch, queryKey]);

    const receivedAt = useSelector((state: UserPageState) => selectReceivedAt(state, queryKey));

    useEffect(() => {
        if (!autoload || !criteria) {
            return;
        }

        if (status === 'idle' || status === 'failed') {
            refetch();
            return;
        }

        if (status === 'succeeded' && receivedAt) {
            const now = Date.now();
            if (now - receivedAt > ttlMs) {
                refetch();
            }
        }
    }, [autoload, criteria, status, queryKey, refetch, receivedAt, ttlMs]);

    return {
        data: items,
        total,
        status,
        error,
        criteria,
        setCriteria,
        setPage,
        setPageSize,
        setSort,
        resetCriteria: reset,
        refetch,
        invalidate,
        isFetching: status === 'loading',
    };
}
